import {
  DELIVERY_FEE_LABEL,
  DELIVERY_FEE_HELP,
} from 'src/utils/deliveryFeeCopy'
import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Form, Input, Select, Collapse, TimePicker, message } from 'antd'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import DeliveryDaysSelect from 'src/components/DeliveryDaysField'
import moment from 'moment'
import { Auth } from 'aws-amplify'
import { API } from 'src/utils/interceptor'

const { Option } = Select
const { Panel } = Collapse

const EditVenueModal = ({ visible, venue, onCancel, onSubmit, form }) => {
  const account = useSelector(state => state.auth.account)
  const suppliers = account?.supplier || []

  const priceFiles = useMemo(
    () =>
      suppliers.map(supplier => ({
        id: supplier._id,
        name: [supplier.title, supplier.secondTitle].filter(Boolean).join(' '),
      })),
    [suppliers],
  )

  const [advancedExpanded, setAdvancedExpanded] = useState(false)
  const [originalPriceFileId, setOriginalPriceFileId] = useState(null)
  const [showPriceFileConfirm, setShowPriceFileConfirm] = useState(false)
  const [pendingValues, setPendingValues] = useState(null)
  const [isMovingVenue, setIsMovingVenue] = useState(false)
  const { getFieldDecorator } = form

  useEffect(() => {
    if (venue && visible) {
      const currentPriceFileId = venue.supplierId || undefined
      setOriginalPriceFileId(currentPriceFileId)
      form.setFieldsValue({
        accountNumber: venue.accountNumber || '',
        priceFile: currentPriceFileId,
        minOrder:
          typeof venue.minimumOrder === 'number'
            ? (venue.minimumOrder / 100).toFixed(2)
            : null,
        deliveryDays: venue.deliveryDays || [],
        cutOffTime: venue.orderCutOff
          ? moment(venue.orderCutOff, 'HH:mm')
          : null,
        deliveryFee:
          typeof venue.deliveryFree === 'number'
            ? (venue.deliveryFree / 100).toFixed(2)
            : null,
        addressLine1: venue.addressLine1 || '',
        addressLine2: venue.addressLine2 || '',
        addressCity: venue.addressCity || '',
        addressPostCode: venue.addressPostCode || '',
      })
      setAdvancedExpanded(false)
      setShowPriceFileConfirm(false)
      setPendingValues(null)
    } else {
      form.resetFields()
      setOriginalPriceFileId(null)
      setShowPriceFileConfirm(false)
      setPendingValues(null)
    }
  }, [venue?._id, venue?.venueSupplierId, visible])

  if (!venue) return null

  const handleSubmit = async () => {
    form.validateFields(async (err, values) => {
      if (!err) {
        // Check if price file changed
        const newPriceFileId = values.priceFile
        const priceFileChanged =
          originalPriceFileId &&
          newPriceFileId &&
          originalPriceFileId !== newPriceFileId

        if (priceFileChanged) {
          // Store values and show confirmation
          setPendingValues(values)
          setShowPriceFileConfirm(true)
          return
        }

        // No price file change, proceed normally
        const deliveryDays = values.deliveryDays || []
        const cutOffTime = values.cutOffTime
          ? moment(values.cutOffTime).format('HH:mm')
          : null

        const payload = {
          venue_supplier_id: venue.venueSupplierId,
          account_number: values.accountNumber.toUpperCase(),
          price_file_id: values.priceFile,
          advanced: {
            min_order: values.minOrder
              ? Math.round(parseFloat(values.minOrder) * 100)
              : null,
            delivery_days: deliveryDays.length > 0 ? deliveryDays : [],
            cut_off: cutOffTime,
            delivery_fee: values.deliveryFee
              ? Math.round(parseFloat(values.deliveryFee) * 100)
              : null,
            addressLine1: values.addressLine1?.trim() || '',
            addressLine2: values.addressLine2?.trim() || '',
            addressCity: values.addressCity?.trim() || '',
            addressPostCode: values.addressPostCode?.trim() || '',
          },
        }
        onSubmit(venue._id, payload)
        form.resetFields()
        setAdvancedExpanded(false)
      }
    })
  }

  const handleConfirmPriceFileChange = async () => {
    if (!pendingValues) return

    setIsMovingVenue(true)
    try {
      const session = await Auth.currentAuthenticatedUser()
      const username = session?.attributes?.sub

      // First, move the venue to the new price file
      const response = await API.post(
        'api',
        `supplier/${pendingValues.priceFile}/assign-venues`,
        {
          body: {
            venueIds: [venue._id],
            username,
          },
        },
      )

      if (response.success) {
        message.success('Venue moved to new price file successfully.')

        // Now proceed with the normal edit
        const deliveryDays = pendingValues.deliveryDays || []
        const cutOffTime = pendingValues.cutOffTime
          ? moment(pendingValues.cutOffTime).format('HH:mm')
          : null

        // After moving, don't pass venueSupplierId - let editCustomer find the active one automatically
        const payload = {
          venue_supplier_id: undefined, // Let backend find the active VenueSupplier
          account_number: pendingValues.accountNumber.toUpperCase(),
          price_file_id: pendingValues.priceFile,
          advanced: {
            min_order: pendingValues.minOrder
              ? Math.round(parseFloat(pendingValues.minOrder) * 100)
              : null,
            delivery_days: deliveryDays.length > 0 ? deliveryDays : [],
            cut_off: cutOffTime,
            delivery_fee: pendingValues.deliveryFee
              ? Math.round(parseFloat(pendingValues.deliveryFee) * 100)
              : null,
            addressLine1: pendingValues.addressLine1?.trim() || '',
            addressLine2: pendingValues.addressLine2?.trim() || '',
            addressCity: pendingValues.addressCity?.trim() || '',
            addressPostCode: pendingValues.addressPostCode?.trim() || '',
          },
        }
        onSubmit(venue._id, payload)
        form.resetFields()
        setAdvancedExpanded(false)
        setShowPriceFileConfirm(false)
        setPendingValues(null)
      } else {
        message.error(
          response.message || 'Failed to move venue. Please try again.',
        )
      }
    } catch (error) {
      console.error('Failed to move venue:', error)
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to move venue. Please try again.'
      message.error(errorMessage)
    } finally {
      setIsMovingVenue(false)
    }
  }

  const handleCancelPriceFileChange = () => {
    setShowPriceFileConfirm(false)
    setPendingValues(null)
  }

  const handleCancel = () => {
    if (showPriceFileConfirm) {
      // If confirmation modal is open, just close it
      handleCancelPriceFileChange()
      return
    }
    form.resetFields()
    setAdvancedExpanded(false)
    onCancel()
  }

  const validateAccountNumber = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Account number is required'))
    }
    const pattern = /^[A-Z0-9\-_/\s]+$/
    if (!pattern.test(value.toUpperCase())) {
      return Promise.reject(
        new Error(
          'Account number can only contain letters, numbers, dashes, underscores, slashes, and spaces',
        ),
      )
    }
    return Promise.resolve()
  }

  // Get price file names for confirmation message
  const currentPriceFileName =
    priceFiles.find(f => f.id === originalPriceFileId)?.name ||
    'current price file'
  const newPriceFileName =
    pendingValues &&
    priceFiles.find(f => f.id === pendingValues.priceFile)?.name

  return (
    <>
      <Modal
        title='Edit Venue'
        visible={visible}
        onCancel={handleCancel}
        onOk={handleSubmit}
        okText='Save changes'
        cancelText='Cancel'
        width={600}
        destroyOnClose
        okButtonProps={{
          loading: isMovingVenue,
          disabled: isMovingVenue,
        }}
        cancelButtonProps={{
          disabled: isMovingVenue,
        }}
        closable={!isMovingVenue}
      >
        <ModalContent>
          <Form layout='vertical' requiredMark={false}>
            <Form.Item label='Venue name'>
              <Input value={venue.venueTitle} disabled />
              <ReadOnlyNote>
                The venue can change their own name in their OMS account.
              </ReadOnlyNote>
            </Form.Item>

            <Form.Item
              label='Account Number'
              help='Match this to the account number you use in your back-office.'
            >
              {getFieldDecorator('accountNumber', {
                rules: [
                  { required: true, message: 'Account number is required' },
                  {
                    validator: validateAccountNumber,
                  },
                ],
              })(
                <Input
                  placeholder='BB-LEITH-001'
                  onChange={e => {
                    const value = e.target.value.toUpperCase()
                    form.setFieldsValue({ accountNumber: value })
                  }}
                />,
              )}
            </Form.Item>

            <Form.Item
              label='Price File'
              help='Choose which price file this venue should use.'
            >
              {getFieldDecorator('priceFile', {
                rules: [
                  {
                    required: true,
                    message: 'Please select a price file',
                  },
                ],
              })(
                <Select placeholder='Select price file'>
                  {priceFiles.map(file => (
                    <Option key={file.id} value={file.id}>
                      {file.name}
                    </Option>
                  ))}
                </Select>,
              )}
            </Form.Item>

            <Collapse
              activeKey={advancedExpanded ? ['advanced'] : []}
              onChange={keys => {
                const isExpanded =
                  Array.isArray(keys) && keys.includes('advanced')
                setAdvancedExpanded(isExpanded)
              }}
              style={{ marginTop: 16 }}
            >
              <Panel header='Advanced setup' key='advanced'>
                <Form.Item label='Minimum Order (£)'>
                  {getFieldDecorator('minOrder')(
                    <Input
                      type='number'
                      min={0}
                      step={0.01}
                      placeholder='0.00'
                      prefix='£'
                    />,
                  )}
                </Form.Item>

                <Form.Item label='Delivery Days'>
                  {getFieldDecorator('deliveryDays', {
                    initialValue: venue?.deliveryDays || [],
                  })(
                    <DeliveryDaysSelect
                      deliveryDays={form.getFieldValue('deliveryDays') || []}
                      onChange={value =>
                        form.setFieldsValue({ deliveryDays: value })
                      }
                    />,
                  )}
                </Form.Item>

                <Form.Item label='Order Cut-Off Time'>
                  {getFieldDecorator('cutOffTime')(
                    <TimePicker
                      format='HH:mm'
                      placeholder='17:00'
                      style={{ width: '100%' }}
                    />,
                  )}
                </Form.Item>

                <Form.Item label={DELIVERY_FEE_LABEL} extra={DELIVERY_FEE_HELP}>
                  {getFieldDecorator('deliveryFee')(
                    <Input
                      type='number'
                      min={0}
                      step={0.01}
                      placeholder='0.00'
                      prefix='£'
                    />,
                  )}
                </Form.Item>

                <Form.Item label='Address line 1'>
                  {getFieldDecorator('addressLine1')(
                    <Input placeholder='123 Street' />,
                  )}
                </Form.Item>

                <Form.Item label='Address line 2 (optional)'>
                  {getFieldDecorator('addressLine2')(
                    <Input placeholder='Suite 4B' />,
                  )}
                </Form.Item>

                <Form.Item label='City / Town'>
                  {getFieldDecorator('addressCity')(
                    <Input placeholder='Edinburgh' />,
                  )}
                </Form.Item>

                <Form.Item label='Postcode'>
                  {getFieldDecorator('addressPostCode')(
                    <Input placeholder='EH1 2AB' />,
                  )}
                </Form.Item>
              </Panel>
            </Collapse>
          </Form>
        </ModalContent>
      </Modal>

      {/* Price File Change Confirmation Modal */}
      <Modal
        title='Confirm Price File Change'
        visible={showPriceFileConfirm}
        onOk={handleConfirmPriceFileChange}
        onCancel={handleCancelPriceFileChange}
        okText='Yes, move venue'
        cancelText='Cancel'
        okButtonProps={{
          loading: isMovingVenue,
          disabled: isMovingVenue,
        }}
        width={500}
      >
        <PriceFileConfirmContent>
          <p>
            You&apos;re about to move <strong>{venue?.venueTitle}</strong> from{' '}
            <strong>{currentPriceFileName}</strong> to{' '}
            <strong>{newPriceFileName}</strong>.
          </p>
          <p>
            This will switch the venue to the new price file. All other changes
            will also be saved.
          </p>
          <p style={{ marginBottom: 0 }}>
            <strong>Do you want to continue?</strong>
          </p>
        </PriceFileConfirmContent>
      </Modal>
    </>
  )
}

const ModalContent = styled.div`
  padding: 8px 0;
`

const ReadOnlyNote = styled.div`
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
  font-style: italic;
`

const PriceFileConfirmContent = styled.div`
  p {
    margin-bottom: 16px;
    line-height: 1.6;
  }
`

const WrappedEditVenueModal = Form.create({ name: 'edit_venue' })(
  EditVenueModal,
)
export default WrappedEditVenueModal
